import { PaletteCategory } from '@libs/types';
import { TCoreInputOptions } from '../types/options';
import { getValueFromPalette, px, TPaletteState } from '../utils/styles';

// TCoreInputOptions.error is typed as a flat color string for the legacy CoreInputView;
// getValueFromPalette expects `error`/`disabled` to be nested palette overrides instead
// (as they already are on the shared TCoreOptions and on every other core widget's palette).
type TStatefulColorPalette = PaletteCategory & {
    error?: Partial<PaletteCategory>;
    disabled?: Partial<PaletteCategory>;
};

export const buildInputCssVarsNew = (palette: TCoreInputOptions, state: TPaletteState = {}): Record<string, string> => {
    const margin = palette.margin ?? {};
    const padding = palette.padding ?? {};
    const statePalette = palette as unknown as TStatefulColorPalette;

    const outline = getValueFromPalette(statePalette, 'outline', state);
    const onSurface = getValueFromPalette(statePalette, 'onSurface', state);
    const onSurfaceVariant = getValueFromPalette(statePalette, 'onSurfaceVariant', state);
    const main = getValueFromPalette(statePalette, 'main', state);
    const surfaceContainerHighest = getValueFromPalette(statePalette, 'surfaceContainerHighest', state);

    const vars: Record<string, string | undefined> = {
        '--ci-radius': px(palette.inputBorderRadius),
        '--ci-height': px(palette.height),
        '--ci-text-align': palette.textAlign,
        // background & text — resolved from the active state (error / disabled / default)
        '--ci-bg': surfaceContainerHighest,
        '--ci-text-color': onSurface,
        '--ci-text-color-hover': palette.hovered?.onSurface,
        '--ci-caret-color': main,
        // outline (container border) — focus isn't part of TPaletteState, so it keeps its own var
        '--ci-outline-color': outline,
        '--ci-outline-color-focus': palette.main,
        // label
        '--ci-label-color': onSurfaceVariant,
        '--ci-label-color-focus': palette.main,
        // active indicator (underline)
        '--ci-indicator-color': onSurfaceVariant,
        '--ci-indicator-color-focus': palette.main,
        // helper text
        '--ci-helper-color': onSurfaceVariant,
        // typography
        '--ci-font-family': palette.font,
        '--ci-font-weight': typeof palette.fontStyle?.fontWeight === 'number' ? palette.fontStyle.fontWeight.toString() : undefined,
        '--ci-font-size': px(palette.fontStyle?.fontSize),
        '--ci-line-height': px(palette.fontStyle?.lineHeight),
        '--ci-font-size-xl': px(palette.fontStyle?.fontSizeXl),
        '--ci-line-height-xl': px(palette.fontStyle?.lineHeightXl),
        '--ci-letter-spacing': px(palette.fontStyle?.letterSpacing),
        // spacing
        '--ci-margin-top': px(margin.top),
        '--ci-margin-right': px(margin.right),
        '--ci-margin-bottom': px(margin.bottom),
        '--ci-margin-left': px(margin.left),
        '--ci-padding-top': px(padding.top),
        '--ci-padding-right': px(padding.right),
        '--ci-padding-bottom': px(padding.bottom),
        '--ci-padding-left': px(padding.left),
    };

    return Object.fromEntries(Object.entries(vars).filter((entry): entry is [string, string] => entry[1] !== undefined));
};
